import { thunkCopySelectedBlock } from '@store/migrationActions/thunkActions/thunkBlocksActions'
import { thunkCopySelectedNodes } from '@store/migrationActions/thunkActions/thunkNodesActions'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { copySelectedStickyNote } from '@storeOld/reducers/stickyNotes'
import { copySelectedSymbol } from '@storeOld/reducers/symbolsLibrary/symbolsLibrary'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import type { AppState } from '@/types'
import { Keys } from '../keyboard'

const useOnCopyKeyboardActions = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  // node selectors
  const selectedNode = useSelector<AppState, string | null>(
    ({ canvas }) =>
      canvas.node.selectedNodes[canvas.node.selectedNodes.length - 1]
  )

  const selectedNodeLink = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedNodeLink
  )

  const selectedToEditNode = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedToEditNode
  )

  // block selectors
  const selectedBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.ids[canvas.block.ids.length - 1]
  )

  const selectedToEditBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.selectedToEditBlockId
  )

  // symbol selectors
  const selectedSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedSymbol
  )

  const selectedToEditSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedToEditSymbol
  )

  // sticky note selectors
  const selectedStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedStickyNote
  )

  const selectedToEditStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedToEditStickyNote
  )

  const showCopyNotification = () => {
    dispatch(notificationActionCreators.setClearAllNotification())
    dispatch(
      notificationActionCreators.setNotification({
        type: NotificationType.COPY_SUCCESSFUL,
      })
    )
  }

  const onCopyNode = (event: KeyboardEvent) => {
    event.preventDefault()
    dispatch(
      thunkCopySelectedNodes({ shiftKey: event.shiftKey && !event.altKey })
    )
    showCopyNotification()
  }

  const onCopyBlock = (event: KeyboardEvent) => {
    event.preventDefault()
    dispatch(thunkCopySelectedBlock())
    showCopyNotification()
  }

  const onCopySymbol = (event: KeyboardEvent) => {
    event.preventDefault()

    // @todo implement action
    // @ts-expect-error
    dispatch(copySelectedSymbol())
    showCopyNotification()
  }

  const onCopyStickyNote = (event: KeyboardEvent) => {
    event.preventDefault()
    dispatch(copySelectedStickyNote())
    showCopyNotification()
  }

  return (event: KeyboardEvent) => {
    if (!(event.ctrlKey || event.metaKey) || event.code !== Keys.Copy) {
      return
    }

    if (selectedNode && !selectedToEditNode && !selectedNodeLink) {
      onCopyNode(event)
    }

    if (selectedBlock && !selectedToEditBlock) {
      onCopyBlock(event)
    }

    if (selectedSymbol && !selectedToEditSymbol) {
      onCopySymbol(event)
    }

    if (selectedStickyNote && !selectedToEditStickyNote) {
      onCopyStickyNote(event)
    }
  }
}

export default useOnCopyKeyboardActions
